import { useSceneStore } from "@/lib/sceneStore";
import {
  AdditiveBlending,
  CanvasTexture,
  Camera,
  Object3D,
  Sprite,
  SpriteMaterial,
  Vector3,
} from "three";

const HINT_IDLE_DELAY_SECONDS = 8;
const SCREEN_EDGE_PADDING_PERCENT = 9;
const ON_SCREEN_MARGIN = 0.86;
const HIGHLIGHT_SCALE = 0.42;
const tmpWorldPosition = new Vector3();
const tmpProjectedPosition = new Vector3();
const tmpCameraSpacePosition = new Vector3();

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

let sharedHighlightTexture: CanvasTexture | null = null;

function getHighlightTexture() {
  if (sharedHighlightTexture) return sharedHighlightTexture;

  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext("2d");

  if (context) {
    const gradient = context.createRadialGradient(64, 64, 8, 64, 64, 62);
    gradient.addColorStop(0, "rgba(255,255,255,0.9)");
    gradient.addColorStop(0.28, "rgba(120,245,255,0.55)");
    gradient.addColorStop(0.58, "rgba(64,196,255,0.22)");
    gradient.addColorStop(1, "rgba(64,196,255,0)");
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(64, 64, 62, 0, Math.PI * 2);
    context.fill();

    context.strokeStyle = "rgba(221,255,255,0.78)";
    context.lineWidth = 5;
    context.beginPath();
    context.arc(64, 64, 38, 0, Math.PI * 2);
    context.stroke();
  }

  sharedHighlightTexture = new CanvasTexture(canvas);
  return sharedHighlightTexture;
}

function isObjectCollectable(object: Object3D) {
  return !object.userData.hasHit && object.parent !== null;
}

function getScreenPosition(object: Object3D, camera: Camera) {
  object.getWorldPosition(tmpWorldPosition);
  tmpProjectedPosition.copy(tmpWorldPosition).project(camera);

  tmpCameraSpacePosition
    .copy(tmpWorldPosition)
    .applyMatrix4(camera.matrixWorldInverse);
  const behindCamera = tmpCameraSpacePosition.z >= 0;
  let ndcX = tmpProjectedPosition.x;
  let ndcY = tmpProjectedPosition.y;

  if (behindCamera) {
    const horizontalDistance = Math.hypot(
      tmpCameraSpacePosition.x,
      tmpCameraSpacePosition.z
    );

    ndcX = tmpCameraSpacePosition.x >= 0 ? 1 : -1;
    ndcY = clamp(
      tmpCameraSpacePosition.y / Math.max(horizontalDistance, 0.001),
      -0.9,
      0.9
    );
  }

  const isOnScreen =
    !behindCamera &&
    tmpProjectedPosition.z >= -1 &&
    tmpProjectedPosition.z <= 1 &&
    Math.abs(ndcX) <= ON_SCREEN_MARGIN &&
    Math.abs(ndcY) <= ON_SCREEN_MARGIN;

  return {
    ndcX,
    ndcY,
    isOnScreen,
    centerDistanceSq: ndcX * ndcX + ndcY * ndcY + (behindCamera ? 4 : 0),
  };
}

function clampToScreenEdge(ndcX: number, ndcY: number) {
  const padding = SCREEN_EDGE_PADDING_PERCENT;
  const xPercent = (ndcX * 0.5 + 0.5) * 100;
  const yPercent = (-ndcY * 0.5 + 0.5) * 100;

  return {
    x: Math.max(padding, Math.min(100 - padding, xPercent)),
    y: Math.max(padding, Math.min(100 - padding, yPercent)),
  };
}

function createHighlightSprite() {
  const material = new SpriteMaterial({
    map: getHighlightTexture(),
    transparent: true,
    opacity: 0.72,
    depthTest: false,
    depthWrite: false,
    blending: AdditiveBlending,
  });
  const sprite = new Sprite(material);
  sprite.layers.set(1);
  sprite.name = "garbage-hint-highlight";
  sprite.renderOrder = 999;
  sprite.scale.setScalar(HIGHLIGHT_SCALE);
  return sprite;
}

export function createGarbageHintTracker(objects: Set<Object3D>, camera: Camera) {
  const highlights = new Map<Object3D, Sprite>();

  const removeHighlight = (object: Object3D) => {
    const highlight = highlights.get(object);
    if (!highlight) return;

    highlight.removeFromParent();
    highlight.material.dispose();
    highlights.delete(object);
  };

  const syncHighlight = (target: Object3D, time: number) => {
    highlights.forEach((_, object) => {
      if (object !== target) removeHighlight(object);
    });

    let highlight = highlights.get(target);
    if (!highlight) {
      highlight = createHighlightSprite();
      highlights.set(target, highlight);
      target.add(highlight);
    }

    const pulse = 0.86 + Math.sin(time * 4.2) * 0.14;
    highlight.scale.setScalar(HIGHLIGHT_SCALE * pulse);
    highlight.material.opacity = 0.54 + Math.sin(time * 3.2) * 0.16;
  };

  const clear = () => {
    highlights.forEach((highlight) => {
      highlight.removeFromParent();
      highlight.material.dispose();
    });
    highlights.clear();
    useSceneStore.getState().clearGarbageHint();
  };

  let hasActivityState = false;
  let lastCollectedCount = 0;
  let lastCollectionTime = 0;
  let currentTarget: Object3D | null = null;

  const selectTarget = (remainingObjects: Object3D[]) => {
    let target: Object3D | null = null;
    let targetScreen = {
      ndcX: 0,
      ndcY: 0,
      isOnScreen: false,
      centerDistanceSq: Number.POSITIVE_INFINITY,
    };

    remainingObjects.forEach((object) => {
      const screen = getScreenPosition(object, camera);
      if (screen.centerDistanceSq < targetScreen.centerDistanceSq) {
        target = object;
        targetScreen = screen;
      }
    });

    return { target, targetScreen };
  };

  return {
    update(time: number) {
      const { collectedGarbageCount, isGamePaused } = useSceneStore.getState();
      if (!hasActivityState) {
        hasActivityState = true;
        lastCollectedCount = collectedGarbageCount;
        lastCollectionTime = time;
      }

      if (collectedGarbageCount !== lastCollectedCount) {
        lastCollectedCount = collectedGarbageCount;
        lastCollectionTime = time;
        currentTarget = null;
        clear();
        return;
      }

      const remainingObjects = Array.from(objects).filter(isObjectCollectable);
      const shouldShow =
        remainingObjects.length > 0 &&
        !isGamePaused &&
        time - lastCollectionTime >= HINT_IDLE_DELAY_SECONDS;

      if (!shouldShow) {
        clear();
        return;
      }

      const selected =
        currentTarget !== null && remainingObjects.includes(currentTarget)
          ? {
              target: currentTarget,
              targetScreen: getScreenPosition(currentTarget, camera),
            }
          : selectTarget(remainingObjects);
      currentTarget = selected.target;

      if (!currentTarget) {
        clear();
        return;
      }

      syncHighlight(currentTarget, time);

      const screenPosition = selected.targetScreen.isOnScreen
        ? {
            x: (selected.targetScreen.ndcX * 0.5 + 0.5) * 100,
            y: (-selected.targetScreen.ndcY * 0.5 + 0.5) * 100,
          }
        : clampToScreenEdge(selected.targetScreen.ndcX, selected.targetScreen.ndcY);

      useSceneStore.getState().setGarbageHint({
        active: true,
        x: screenPosition.x,
        y: screenPosition.y,
        angle: Math.atan2(-selected.targetScreen.ndcY, selected.targetScreen.ndcX),
        isOnScreen: selected.targetScreen.isOnScreen,
        remainingCount: remainingObjects.length,
      });
    },
    dispose: clear,
  };
}
